/**
 * Rollout target search (Phase 4 item 7 follow-up, 2026-09-14).
 *
 * A tick-list is fine for a lab and useless for a site with thousands of
 * phones. The picker filters the image's candidate devices by a free-text
 * query matched against extension, MAC (any separators), label and model,
 * and can hide devices already running the image's version. Pure so it is
 * unit-tested without React.
 */

import type { DeviceRow } from "@pbxmgr/api";

/** How many matches the picker renders before asking for a narrower query. */
export const RENDER_CAP = 200;

/** Lower-case hex only, so `00:04:13:E3:23:0B`, `0004.13e3.230b` and `000413e3230b` compare equal. */
export function normalizeMac(value: string): string {
  return value.toLowerCase().replace(/[^0-9a-f]/g, "");
}

export interface TargetFilter {
  query: string;
  /** When set, devices whose `firmware_current` equals this version are hidden. */
  excludeVersion?: string | null;
}

/** Whether one device matches the query (empty query matches everything). */
export function matchesQuery(device: DeviceRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const qMac = normalizeMac(q);
  if (qMac.length >= 4 && normalizeMac(device.mac).includes(qMac)) return true;
  if ((device.extensions ?? []).some((e) => e.toLowerCase().includes(q)))
    return true;
  if ((device.label ?? "").toLowerCase().includes(q)) return true;
  return device.model.toLowerCase().includes(q);
}

/** The candidate devices that pass the filter, in the input order. */
export function filterTargets(
  devices: DeviceRow[],
  filter: TargetFilter,
): DeviceRow[] {
  return devices.filter(
    (d) =>
      matchesQuery(d, filter.query) &&
      !(filter.excludeVersion && d.firmware_current === filter.excludeVersion),
  );
}

/** One-line identity for a device in the picker: label · extensions · MAC · running version. */
export function describeTarget(device: DeviceRow): string {
  const exts = (device.extensions ?? []).join("/");
  const parts = [
    device.label ?? "",
    exts ? `ext ${exts}` : "",
    device.mac,
  ].filter(Boolean);
  return `${parts.join(" · ")} · running ${device.firmware_current ?? "unknown"}`;
}
